import _ from '@/common/helpers';

const vuelidateKeys = {
    $each: '$each',
    $iter: '$iter',
};

const ignoredPathLabelKeys = Object.values(vuelidateKeys);

/**
 * Returns field path label.
 *
 * @description
 * const path = ['order', 'billingAnchor', 'billingAnchorInstruction', 'duration'];
 * const label = makePathLabel(path);
 *
 * label === Order billing anchor billing anchor instruction duration
 * TODO: label capitalize or not?
 * Order Billing Anchor Billing Anchor Instruction Duration
 * vs Order billing anchor billing anchor instruction duration
 *
 * @param {array} path
 * @return {string} {*}
 */
const makePathLabel = path => path
    .filter(p => !ignoredPathLabelKeys.includes(p))
    .map((item, i) => {
        const label = _.unCamelCase(_.unKebabCase(item));
        return i === 0 ? _.capitalize(label) : label.toLowerCase();
    }).join(' ');

/**
 * Returns string field error message.
 *
 * @param {string} template - `{label} should be {key} or {key2} ...`
 * @param {object} args - {label, key, key2, ...}
 * @return {string}
 */
const formatMessage = ({template, args}) => Object.keys(args)
    .reduce((memo, key) => memo.replace(`{${key}}`, args[key]), template);

/**
 * Returns error message data template string template and object args
 * @param {string} label - field label
 * @param {string} validator - string name validator from vuelidate
 * @param {object} field - vuelidate field $v.entity.field
 * @return {object} - {{}template, []args}
 */
const getErrorMessageData = ({label, validator, field}) => {
    switch (validator) {
        case 'required':
            return {
                template: `{label} is required.`,
                args: {label},
            };
        case 'email':
            return {
                template: `{label} should be an email`,
                args: {label},
            };
        case 'url':
            return {
                template: `{label} should be valid url`,
                args: {label},
            };
        case 'numeric':
            return {
                template: `{label} should be a number`,
                args: {label},
            };
        case 'decimal':
            return {
                template: `{label} should be a number`,
                args: {label},
            };
        case 'minLength':
            return {
                template: `{label} should have at least {min} characters long.`,
                args: {label, min: field.$params.minLength.min},
            };
        case 'minValue':
            return {
                template: `{label} should be at least {min}.`,
                args: {label, min: field.$params.minValue.min},
            };
        case 'sameAs':
            // TODO: get label to sameAs field from component argument
            return {
                template: `{label} should be eq {sameAsLabel}`,
                args: {label, sameAsLabel: makePathLabel(field.$params.sameAs.eq)},
            };
        case 'between':
            return {
                template: `{label} should be between {min} and {max}`,
                args: {label, min: field.$params.between.min, max: field.$params.between.max},
            };
        default:
            // implement new validators
            return {
                template: `{label} should be ${_.unKebabCase(_.unCamelCase(validator)).toLowerCase()}`,
                args: {label},
            };
    }
};

/**
 * Returns array with error fields from $v (recursive)
 * @param {object} $v
 * @param {array} parentPath - parent path
 * @param {bool} isArrayFieldLevel
 * @return {Array}
 */
const getRecursiveErrors = ($v, parentPath = [], isArrayFieldLevel) => {
    if (!$v || !$v.$params) {
        return [];
    }

    /**
     * Returns true if it's field last level child item
     *
     * validations = {user:{contacts:{phone}}}
     * user - not field
     * contacts - not field
     * phone - field
     *
     * @param {object} field
     * @return {boolean}
     */
    const isField = (field) => {
        // customized validation can be in other format and field might be bool
        if (!field || !_.isObject(field) || !field.$params) {
            return false;
        }
        return !!(Object.keys(field.$params).find(name => field.$params[name] !== null));
    };

    /**
     * Returns prepared field output (1 element of resultative array)
     * @param id
     * @param name
     * @param parent
     * @param path
     * @param validator
     * @param field
     */
    const makeField = ({
        id, name, parent, path, validator, field,
    }) => ({
        id, // field id, 'user.userName' DOESN'T UNIQUE
        name, // field name 'userName'
        parent, // parent field or array index in array fields
        path, // field path array [user, userName]
        label: makePathLabel(path), // string path label 'User username'
        validator, // validator name "required", "sameAs", "numeric", etc.
        field, // vuelidate field object $.user.userName
        rule: `${id}.${validator}`, // field id with validator 'entity.price.required' or 'entity.price.numeric'
        predicate: () => (field.$invalid || field.$error) && !field[validator],
    });

    return Object.keys($v.$params)
        .filter(name => $v.$params[name] === null)
        .reduce((memo, name) => {
            const isArrayItems = name === vuelidateKeys.$each; // values will be array
            const path = isArrayFieldLevel ? [...parentPath] : [...parentPath, name];
            const parent = parentPath[parentPath.length - 1];

            // push iterator key if it's $each vuelidate level
            if (isArrayItems) {
                path.push(vuelidateKeys.$iter);
            }

            const id = path.join('.');
            const field = $v[name];

            if (isField(field)) {
                return [
                    ...memo,
                    ...Object.keys(field.$params).map(validator => makeField({
                        id,
                        name,
                        parent,
                        path,
                        validator,
                        field,
                    })),
                    ...getRecursiveErrors(field, path, isArrayItems),
                ];
            }
            // it's customized validator, so field is validator parent
            if (field.$params === undefined) {
                return [
                    ...memo,
                    makeField({
                        id: parentPath.join('.'),
                        name: parent,
                        parent,
                        path: parentPath,
                        validator: name,
                        field: $v,
                    }),
                    ...getRecursiveErrors(field, path, isArrayItems),
                ];
            }
            return [
                ...memo,
                ...getRecursiveErrors(field, path, isArrayItems),
            ];
        }, []);
};

/**
 * Returns array with errors
 *
 * @param {object} $v - component.$v
 * @return {array}
 */
export default function getVuelidatePredicates($v) {
    return getRecursiveErrors($v)
        .map(({
            name, label, path, validator, field, ...data
        }) => {
            const {template, args} = getErrorMessageData({
                name,
                path,
                label,
                validator,
                field,
                component: $v,
            });
            return {
                ...data,
                name,
                path,
                label,
                validator,
                field,
                template,
                args,
                format: formatMessage,
                message: formatMessage({template, args}),
            };
        });
}
